import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GroupByOption, ITileComponent, StoredMetric } from '../common-tile.model';
import { CalcEngineConstants, CalcEngineRequest, CalcEngineResponse, Metric } from 'src/app/services/models/calc-engine.model';
import { UserBoardComponentMode } from 'src/app/services/models/user-board.model';
import { SelectedFilterUpdatesService } from 'src/app/common/bhub-common-module/services/selected-filter-updates.service';
import { CalcEngineService } from 'src/app/services/calc-engine.service';
import { MetricService } from 'src/app/services/metric.service';
import { ChartMetric, StoredChartTileDefinition } from './chart-tile.model';
import { DatasetService } from 'src/app/services/dataset.service';
import { Dataset, DatasetType } from 'src/app/services/models/datasets.models';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'bhub-chart-tile',
  standalone: false,
  templateUrl: './chart-tile.component.html',
  styleUrl: './chart-tile.component.scss'
})
export class ChartTileComponent implements OnInit, ITileComponent {
  @Input() data: StoredChartTileDefinition;
  @Input() mode: UserBoardComponentMode;

  @Output() onPreviewItemClickEvent = new EventEmitter<string>();

  //Make enum available for html
  UserBoardComponentMode = UserBoardComponentMode;
  //This needs to come from a service instead
  availableMetrics: StoredMetric[];
  filtersApplied: any = {};

  model: StoredChartTileDefinition = new StoredChartTileDefinition("", null, [], []);

  public initialized = false;
  public chartData: any = {};
  public chartOptions: any = {};
  public datasets: Dataset[] = [];
  public selectedDataset: Dataset | null = null;
  public availableFields: {fieldId: number, fieldName: string}[] = [];
  public barsStacked: boolean = false;

  selectedLineMetric: StoredMetric | null = null;
  solidOrDashed: string = 'Solid';
  selectedLineColor: string = '#3f51b5'; // default blueish

  selectedBarMetric: StoredMetric | null = null;
  selectedBarColor: string = '#3f51b5'; // default blueish

  private mockLabels = ["Group1", "Group2", "Group3", "Group4", "Group5"];

  constructor(
    private calcEngineService: CalcEngineService,
    private metricService: MetricService,
    private selectedFilterUpdateObserver: SelectedFilterUpdatesService,
    private datasetService: DatasetService,
  ) {
  }

  public validate(): string{
    if(this.model.groupBy.datasetId == ''){
      return 'Please select a dataset';
    } else if(this.model.groupBy.datasetPrimaryKey == '' || this.model.groupBy.fieldToDisplay == '' || this.model.groupBy.fieldIdToDisplay == ''){
      return 'Please select a field to group by';
    }
    else if(this.model.lineMetrics.length == 0 && this.model.barMetrics.length == 0){
      return 'Please add at least one metric to the chart';
    }
    else {
      return '';
    }
  }

  public ngOnInit() {
    //Set initial state of the filters as well as capture updates and make service calls when they change
    this.selectedFilterUpdateObserver.filters$.subscribe((filters) => {
      this.filtersApplied = filters;
      this.makeServiceRequest();
    });

    if(this.mode === UserBoardComponentMode.Edit){
      this.datasetService.getDatasetReferences().subscribe((datasets: Map<number, Dataset>) => {
        this.datasets = Array.from(datasets.values()).filter((dataset) => dataset.datasetReferenceTable.datasetType === DatasetType.Reference || dataset.datasetReferenceTable.datasetType === DatasetType.View);
      });
    }
  }

  onDatasetChange(datasetId: number | string): void {
    this.selectedDataset = this.datasets.find(d => d.datasetReferenceTable.datasetId === Number(datasetId));
    this.availableFields = this.selectedDataset ? this.selectedDataset.dataTableSchemas.map(schema => ({fieldId: schema.fieldId, fieldName: schema.displayName})) : [];

    this.model.groupBy.datasetPrimaryKey = ''; // Clear previous primary key
    this.model.groupBy.fieldToDisplay = ''; // Clear previous selection
    this.model.groupBy.fieldIdToDisplay = ''; // Clear field ID to display
  }

  onFieldChange(fieldName: string): void {
    if (!this.selectedDataset) return;
    
    // Find the primary key field(s)
    const primaryKeyFields = this.selectedDataset.dataTableSchemas.filter(schema => schema.isPrimaryKey);
    if (primaryKeyFields.length === 0) return;
    
    const primaryKeyFieldId = primaryKeyFields[0].fieldId.toString();
    
    // When a field is selected by name, update the primary key and field to display
    const selectedField = this.availableFields.find(f => f.fieldName === fieldName);
    if (selectedField) {
      const fieldIdToDisplay = selectedField.fieldId.toString();
      
      this.model.groupBy.datasetPrimaryKey = primaryKeyFieldId;
      this.model.groupBy.fieldIdToDisplay = fieldIdToDisplay;
      this.model.groupBy.fieldToDisplay = fieldName;
    } else if (fieldName === CalcEngineConstants.PRIMARY_KEY) {
      // Handle primary key selection
      this.model.groupBy.datasetPrimaryKey = CalcEngineConstants.PRIMARY_KEY;
      this.model.groupBy.fieldIdToDisplay = CalcEngineConstants.PRIMARY_KEY;
      this.model.groupBy.fieldToDisplay = fieldName;
    }
  }

  showPrimaryKey(): boolean{
    if(this.selectedDataset == null) return false;
    return this.selectedDataset.dataTableSchemas.filter(schema => schema.isPrimaryKey).map(schema=>schema.displayName).length > 1;
  }

  getPrimaryKeyValue(){
    return CalcEngineConstants.PRIMARY_KEY;
  }

  makeServiceRequest(): void{
    this.initialized = false;

    if(this.mode === UserBoardComponentMode.Live){
      var request = this.generateTileRequest(this.data);
      this.calcEngineService.runCalcEngine(request, false).subscribe( (response) => {
        this.buildModelFromResponse(this.data, response);
        this.initialized = true;
      });
    }
    else if( this.mode === UserBoardComponentMode.Preview) {
      this.buildModelFromMockResponse(this.data);
      this.initialized = true;
    }
    else if( this.mode === UserBoardComponentMode.Edit) {
      if(this.data != null){
        this.buildInputModel(this.data);
        this.initialized = true;
      } else{
        this.buildInputModel();
        this.initialized = true;
      }
    }
  }


  generateTileRequest(data: StoredChartTileDefinition): CalcEngineRequest {
    const request = new CalcEngineRequest();

    // Use primary key for the groupBy
    request.groupBy.push(data.groupBy.datasetPrimaryKey.toString());
    
    // Add to additionalFields if fieldIdToDisplay is different from primary key
    if (data.groupBy.fieldIdToDisplay !== data.groupBy.datasetPrimaryKey) {
      request.additionalFields[data.groupBy.datasetId] = [data.groupBy.fieldIdToDisplay];
    }
    
    request.metricList = data.lineMetrics.map((chartMetric) => chartMetric.metric as Metric);
    request.metricList = request.metricList.concat(data.barMetrics.map((chartMetric) => chartMetric.metric as Metric));
    request.filters = this.filtersApplied;

    return request;
  }



  // Build a new model from the service response and data
  buildModelFromResponse(data: StoredChartTileDefinition, response: CalcEngineResponse): void {
    // Generate labels from the fieldIdToDisplay - this will show the user-selected field values
    const fieldIdToDisplay = data.groupBy.fieldIdToDisplay.toString();
    
    // Use fieldIdToDisplay to get the correct values (primary key values or additional field values)
    this.chartData["labels"] = response.rowData.map(row => row[fieldIdToDisplay] || 'Unknown');
    this.chartData["datasets"] = [];
    
    data.lineMetrics.forEach((chartMetric: ChartMetric) => {
      this.chartData["datasets"].push({
        type: 'line',
        label: chartMetric.metric.metricName,
        borderColor: chartMetric.color,
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        stack: chartMetric.stack,
        borderDash: chartMetric.lineType === 'Dashed' ? [5, 5] : [],
        data: response.rowData.map((row) => row[chartMetric.metric.metricName])
      });
    });

    data.barMetrics.forEach((chartMetric: ChartMetric) => {
      this.chartData["datasets"].push({
        type: 'bar',
        label: chartMetric.metric.metricName,
        backgroundColor: chartMetric.color,
        data: response.rowData.map((row) => row[chartMetric.metric.metricName]),
        borderColor: 'grey',
        borderWidth: 1,
        stack: chartMetric.stack,
      });
    });
    
    this.sortDatasets();
    this.model = data;
    this.chartOptions = this.buildChartOptions();
  }


  // Build a new model from the no call to backend but using structure of tile to build the model
  buildModelFromMockResponse(data: StoredChartTileDefinition): void {
    this.chartData["labels"] = this.mockLabels;
    this.chartData["datasets"] = [];
    data.lineMetrics.forEach((chartMetric: ChartMetric) => {
      this.chartData["datasets"].push({
        type: 'line',
        label: chartMetric.metric.metricName,
        borderColor: chartMetric.color,
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        stack: chartMetric.stack,
        borderDash: chartMetric.lineType === 'Dashed' ? [5, 5] : [],
        data:
          this.mockLabels.reduce((acc, label) => {
            // Generate random data for each label
            acc[label] = Math.random() * 100;
            return acc;
          }, {} as Record<string, number>)

      });
    });

    data.barMetrics.forEach((chartMetric: ChartMetric) => {
      this.chartData["datasets"].push({
        type: 'bar',
        label: chartMetric.metric.metricName,
        backgroundColor: chartMetric.color,
        data:
          this.mockLabels.reduce((acc, label) => {
            // Generate random data for each label
            acc[label] = Math.random() * 100;
            return acc;
          }, {} as Record<string, number>),
        borderColor: 'grey',
        borderWidth: 1,
        stack: chartMetric.stack,
      });
    });
    this.sortDatasets();
    this.model = data;
    this.chartOptions = this.buildChartOptions();
  }

  // Build a new model from scratch
  buildInputModel(data: StoredChartTileDefinition = null): void {

    this.metricService.getMetricList().subscribe((metrics) => {

        //set convert service response to available metrics list
        this.availableMetrics = [];
        metrics.forEach((metric) => {
          this.availableMetrics.push(new StoredMetric(metric.metricId, metric.metricName));
          this.selectedLineMetric = this.availableMetrics[0];
          this.selectedBarMetric = this.availableMetrics[0];
        });

        if(data == null){
          this.chartData["labels"] = this.mockLabels;
          this.chartData["datasets"] = [];
          this.model.lineMetrics = [];
          this.model.barMetrics = [];
          this.model.groupBy = new GroupByOption("", "", "", "");
          this.chartOptions = this.buildChartOptions();
        }
        else {
          this.chartData["labels"] = this.mockLabels;
          this.chartData["datasets"] = [];
          data.lineMetrics.forEach((chartMetric: ChartMetric) => {
            this.chartData["datasets"].push({
              type: 'line',
              label: chartMetric.metric.metricName,
              borderColor: chartMetric.color,
              borderWidth: 2,
              fill: false,
              tension: 0.4,
              stack: chartMetric.stack,
              borderDash: chartMetric.lineType === 'Dashed' ? [5, 5] : [],
              data: this.mockLabels.reduce((acc, label) => {
                // Generate random data for each label
                acc[label] = Math.random() * 100;
                return acc;
              }, {} as Record<string, number>)
            });
          });

          data.barMetrics.forEach((chartMetric: ChartMetric) => {
            this.chartData["datasets"].push({
              type: 'bar',
              label: chartMetric.metric.metricName,
              backgroundColor: chartMetric.color,
              data: this.mockLabels.reduce((acc, label) => {
                // Generate random data for each label
                acc[label] = Math.random() * 100;
                return acc;
              }, {} as Record<string, number>),
              borderColor: 'grey',
              borderWidth: 1,
              stack: chartMetric.stack,
            });
          });
          this.sortDatasets();
          this.model = data;
          this.chartOptions = this.buildChartOptions();
        }

        /// TODO Consider adding logic to present text to user telling them to create metrics if none are available
    });
  }

  buildChartOptions(): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      // aspectRatio: 0.8,
      plugins: this.buildPlugins(),
      scales: this.buildScales()
    };
  }

  buildPlugins(): any {
    return {
        tooltip: {
            mode: 'index',
            intersect: false
        },
        legend: {
            labels: {
                color: 'black'
            }
        }
    }
  }

  buildScales(): any {
    return {
        x: {
          stacked: true,
          ticks: {
              color: 'black'
          },
          grid: {
              color: 'grey'
          }
        },
        y: {
          stacked: true,
          ticks: {
              color: 'black'
          },
          grid: {
              color: 'grey'
          }
        }
    }
  }

  toggleStacked(event: MatSlideToggleChange): void {
    this.barsStacked = event.checked;
    this.chartData["datasets"].map((dataset, index) => {
      if(dataset['type'] === 'line') {
        dataset['stack'] = String(index); // Clear stack for line datasets
      } else {
        dataset['stack'] = this.barsStacked ? 'stack1' : String(index);
      }
      return dataset;
    });
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets] // new reference
    };
  }

  sortDatasets(): void {
    this.chartData["datasets"].sort((a, b) => {
      const typeOrder = { line: 0, bar: 1 };
      return typeOrder[a.type] - typeOrder[b.type];
    });
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets] // new reference
    };
  }

  //Edit Mode methods:
  onLineMetricAdd(metric: StoredMetric, color: string, solidOrDashed: string): void {
    // Find the selected metric based on the selected metricId
    var nextIndex = this.chartData["datasets"].length;
    this.model.lineMetrics.push(new ChartMetric(color, metric, String(nextIndex), solidOrDashed));

    this.chartData["datasets"].push({
      type: 'line',
      label: metric.metricName,
      borderColor: color,
      borderWidth: 2,
      borderDash: solidOrDashed === 'Dashed' ? [5, 5] : [], // Set dashed line style if applicable
      fill: false,
      tension: 0.4,
      data: this.mockLabels.reduce((acc, label) => {
        // Generate random data for each label
        acc[label] = Math.random() * 100;
        return acc;
      }, {} as Record<string, number>),
      stack: String(nextIndex)
    });

    this.sortDatasets();
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets] // new reference
    };
  }

  onBarMetricAdd(metric: StoredMetric, color: string): void {
    // Find the selected metric based on the selected metricId
    var nextIndex = this.chartData["datasets"].length;
    this.model.barMetrics.push(new ChartMetric(color, metric, String(nextIndex), ''));
    this.chartData["datasets"].push({
      type: 'bar',
      label: metric.metricName,
      backgroundColor: color,
      data: this.mockLabels.reduce((acc, label) => {
        // Generate random data for each label
        acc[label] = Math.random() * 100;
        return acc;
      }, {} as Record<string, number>),
      borderColor: 'grey',
      borderWidth: 1,
      stack: this.barsStacked ? 'stack1' : String(nextIndex) // Use the next index for stacking
    });

    this.sortDatasets();
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets] // new reference
    };
  }

  onMetricRemove(index: number, lineOrBar: string): void {

    if(lineOrBar === 'line') {
      this.model.lineMetrics.splice(index, 1);
      this.chartData["datasets"].splice(index, 1);
    }
    else if(lineOrBar === 'bar') {
      this.model.barMetrics.splice(index, 1);
      this.chartData["datasets"].splice(index, 1);
    }
    this.sortDatasets();
    this.chartData = {
      ...this.chartData,
      datasets: [...this.chartData.datasets] // new reference
    };
  }

  onPreviewButtonClick(iconText: string) {
    this.onPreviewItemClickEvent.emit( iconText );
  }
}
